/**
 * 네이버페이 단건결제 SDK 어댑터.
 *
 * merchantUserKey는 Flutter bridge로 userNo 받아 `onl_{userNo}` 패턴으로 생성.
 */

import { NAVERPAY_SDK_URL } from '../constants'

import { loadScript } from './loadScript'

interface NaverPayCreateParams {
  mode: 'production' | 'development'
  payType: 'normal'
  clientId: string
  chainId: string
  openType?: 'page' | 'popup'
}

interface NaverPayOpenParams {
  merchantUserKey: string
  merchantPayKey: string
  productName: string
  totalPayAmount: number
  taxScopeAmount: number
  taxExScopeAmount: number
  productCount: number
  returnUrl: string
  productItems: Array<{
    categoryType: string
    categoryId: string
    uid: string
    name: string
    count: number
  }>
}

interface NaverPayInstance {
  open: (params: NaverPayOpenParams) => void
}

interface NaverPayNamespace {
  Pay: {
    create: (params: NaverPayCreateParams) => NaverPayInstance
  }
}

declare global {
  interface Window {
    Naver?: NaverPayNamespace
  }
}

interface OpenNaverpayRequest {
  amount: number
  orderId: string
  productName: string
  productUid: string
  productCount: number
  merchantUserKey: string
  returnUrl: string
  categoryType?: string
  categoryId?: string
}

export async function openNaverpay(
  clientId: string,
  chainId: string,
  mode: 'production' | 'development',
  request: OpenNaverpayRequest
): Promise<void> {
  if (!clientId || !chainId) throw new Error('NaverPay clientId/chainId is missing')
  if (!request.merchantUserKey) {
    throw new Error('NaverPay merchantUserKey is missing (userNo bridge에서 받아와야 함)')
  }

  await loadScript(NAVERPAY_SDK_URL)
  if (!window.Naver?.Pay) {
    throw new Error('NaverPay SDK not available on window')
  }
  const oPay = window.Naver.Pay.create({
    mode,
    payType: 'normal',
    clientId,
    chainId,
    openType: 'page',
  })
  oPay.open({
    merchantUserKey: request.merchantUserKey,
    merchantPayKey: request.orderId,
    productName: request.productName,
    totalPayAmount: request.amount,
    taxScopeAmount: request.amount,
    taxExScopeAmount: 0,
    productCount: request.productCount,
    returnUrl: request.returnUrl,
    productItems: [
      {
        categoryType: request.categoryType ?? 'ETC',
        categoryId: request.categoryId ?? 'ETC',
        uid: request.productUid,
        name: request.productName,
        count: request.productCount,
      },
    ],
  })
}

/** 네이버페이 returnUrl query parameters */
export interface NaverpayReturnQuery {
  resultCode: string
  paymentId: string
  resultMessage: string
}

export function parseNaverpayReturnQuery(search: URLSearchParams): NaverpayReturnQuery | null {
  const resultCode = search.get('resultCode')
  const paymentId = search.get('paymentId')
  const resultMessage = search.get('resultMessage') ?? ''
  if (!resultCode || !paymentId) return null
  return { resultCode, paymentId, resultMessage }
}

export function isNaverpaySuccess(query: NaverpayReturnQuery): boolean {
  return query.resultCode === 'Success'
}
